import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { FunnelComponentProps } from "../funnel/types";
import { CSSProperties, useEffect, useState } from "react";
import { t } from "i18next";
import { INTERESTS_QUERY_KEY, OnboardingContent } from "./OnboardingContent";
import { apiClient } from "../../services/axiosInstance";
import { useQueryClient } from "@tanstack/react-query";
import { Account, ServerSingleResponse } from "../../types";
import { AxiosResponse } from "axios";
import { useAccountData } from "../../hooks/useAccountData";
import { GET_ACCOUNT_ME_QUERY_KEY, getAccountMe } from "../auth/api/authApi";
import { motion } from "framer-motion";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints";
import { OnboardingStepTitles } from "./OnboardingStepTitles";
import { useAttributionParams } from "../../hooks/useAttributionParams";
import { clearUserArchetype, setUserArchetype } from "./api";

const CLASSIC_ONBOARDING_STEP_MAP = {
  comic_types: 0,
  comic_genres: 1,
  comic_facets: 2,
  splash: 3,
};

const NEW_ONBOARDING_STEP_MAP = {
  archetype: 0,
  splash: 1,
};
export function OnboardingModalBody({
  onSuccess,
  newOnboarding = false,
}: FunnelComponentProps & { newOnboarding?: boolean }) {
  const attributionQueryParams = useAttributionParams();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedArchetypeSlug, setSelectedArchetypeSlug] = useState<string>();
  const accountData = useAccountData();

  const isNewOnboarding = newOnboarding;
  const progressBarSteps = isNewOnboarding ? 1 : 3;
  const lastContentStep = isNewOnboarding
    ? NEW_ONBOARDING_STEP_MAP.archetype
    : CLASSIC_ONBOARDING_STEP_MAP.comic_facets;
  const splashStep = isNewOnboarding
    ? NEW_ONBOARDING_STEP_MAP.splash
    : CLASSIC_ONBOARDING_STEP_MAP.splash;

  useEffect(() => {
    window.document.getElementById("gcModalScrollContainer")?.scrollTo(0, 0);
  }, [activeStep]);

  useEffect(() => {
    setSelectedArchetypeSlug(accountData?.archetype?.slug);
  }, [accountData?.archetype?.slug]);

  // Update the global account data and return user data through callback
  const saveAndClose = (
    accountResponse?: AxiosResponse<ServerSingleResponse<Account>>,
  ) => {
    const interests = queryClient.getQueryData([
      INTERESTS_QUERY_KEY,
    ]) as AxiosResponse<ServerSingleResponse<Account["interests"]>>;
    if (accountData && interests) {
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, {
        data: {
          payload: {
            results: {
              ...accountData,
              interests: interests?.data.payload.results,
            },
          },
        },
      });
    }

    localStorage.setItem("lastOnboardingTime", Date.now().toString());

    // Return user data through onSuccess callback if provided
    if (accountResponse) {
      onSuccess?.({
        data: [accountResponse, null],
      });
    }
  };

  const skipOnboarding = async () => {
    if (accountData?.user?.onboarding !== "completed") {
      await apiClient.post("/v1/account/onboarding/skip", {
        meta: [],
        payload: {},
      });
    }
    const response = await getAccountMe(attributionQueryParams);
    queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, response);

    saveAndClose(response);
  };

  const goToNextStep = async () => {
    if (activeStep === lastContentStep) {
      if (isNewOnboarding && selectedArchetypeSlug) {
        // New flow: set archetype (this also completes onboarding)
        await setUserArchetype(selectedArchetypeSlug);
      } else if (!isNewOnboarding) {
        // Classic flow: complete onboarding
        await apiClient.post("/v1/account/onboarding/complete", {
          meta: [],
          payload: {},
        });
      }

      const response = await getAccountMe(attributionQueryParams);
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, response);

      saveAndClose(response);
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const isAdmin = !!accountData?.user?.is_admin;

  const handleClearArchetype = async () => {
    await clearUserArchetype();
    const response = await getAccountMe(attributionQueryParams);
    queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, response);
    setSelectedArchetypeSlug(undefined);
  };

  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();

  const headerBackdropProps: CSSProperties = isSmall
    ? {
        background: "rgba(20, 21, 23, 0.80)",
        backdropFilter: "blur(18px)",
      }
    : {};

  const showSplash = activeStep === splashStep;
  const canContinue = !isNewOnboarding || !!selectedArchetypeSlug;
  return (
    <Flex
      overflow="hidden"
      direction="column"
      width="100%"
      height={isMobile ? "100dvh" : undefined}
      maxHeight={isSmall && !isMobile ? "calc(90vh - 32px)" : undefined}
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",

        // THE MAGIC:
        // "circle at 50% 275%" -> Center the light slightly below the bottom edge
        // "purple.500" -> The glow color
        // "transparent 70%" -> Where the glow completely disappears
        background:
          "radial-gradient(circle at 50% 275%, var(--chakra-colors-blaze-600), transparent 70%)",

        // Optional: Add a mix-blend-mode for better color interaction with the dark bg
        mixBlendMode: "screen",
      }}
    >
      <Flex
        flexDirection="column"
        flexShrink={0}
        width="100%"
        py={isMobile ? "16px" : "24px"}
        pt={isMobile ? "24px" : "32px"}
        px={isMobile ? "24px" : "48px"}
        justifyContent="space-between"
        zIndex={1}
        style={{ ...headerBackdropProps }}
      >
        {!isNewOnboarding && (
          <>
            <Flex gap="16px">
              {Array.from({ length: progressBarSteps }, (_, index) => (
                <Box
                  key={index}
                  as="button"
                  maxWidth={isMobile ? undefined : "92px"}
                  flex={1 / progressBarSteps}
                  height="1px"
                  borderBottom="2px solid"
                  borderColor={
                    index <= activeStep ? "blaze.blaze" : "neutral.500"
                  }
                  _hover={{
                    borderColor:
                      index <= activeStep ? "blaze.300" : "neutral.300",
                  }}
                  transition="border-color .25s linear"
                  onClick={() => setActiveStep(index)}
                />
              ))}
            </Flex>
            {isMobile && (
              <Button
                gc-testing-id="btnOnboardingSkip"
                variant="iconOnly"
                onClick={skipOnboarding}
                alignSelf="flex-end"
                mt="8px"
              >
                <Text variant="onboardingSkip">
                  {t("components.onboarding.skip")}
                </Text>
              </Button>
            )}
          </>
        )}
      </Flex>
      <Box flexShrink={0}>
        <OnboardingStepTitles
          activeStep={activeStep}
          isNewOnboarding={isNewOnboarding}
        />
      </Box>
      <Box
        key={activeStep}
        className="styled-scrollbar"
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition=".5s linear"
        p={isNewOnboarding || isMobile ? 0 : "24px"}
        px={isMobile ? "16px" : undefined}
        py={isMobile ? "16px" : undefined}
        paddingTop={showSplash ? 0 : isNewOnboarding ? "8px" : undefined}
        paddingBottom={showSplash ? 0 : "16px"}
        minHeight={
          showSplash && isMobile ? "calc(100vh - 48px)" : 0
        }
        display="flex"
        flexDirection="column"
        gap={showSplash ? 0 : isMobile ? "24px" : undefined}
        flex={isSmall ? "1 1 auto" : undefined}
        overflowY={isSmall ? "auto" : undefined}
        overflowX={isSmall ? "hidden" : undefined}
      >
        <Flex flexGrow={1} alignItems="center">
          <OnboardingContent
            activeStep={activeStep}
            onSuccess={goToNextStep}
            isNewOnboarding={isNewOnboarding}
            selectedArchetypeSlug={selectedArchetypeSlug}
            onArchetypeSelect={setSelectedArchetypeSlug}
          />
        </Flex>
      </Box>
      {!showSplash ? (
        <OnboardingNavigationButtons
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          goToNextStep={goToNextStep}
          skipOnboarding={isNewOnboarding ? undefined : skipOnboarding}
          isLastContentStep={activeStep === lastContentStep}
          canContinue={canContinue}
          isNewOnboarding={isNewOnboarding}
          isAdmin={isAdmin}
          onClearArchetype={handleClearArchetype}
        />
      ) : null}
    </Flex>
  );
}

function OnboardingNavigationButtons({
  activeStep,
  setActiveStep,
  goToNextStep,
  skipOnboarding,
  isLastContentStep,
  canContinue,
  isNewOnboarding,
  isAdmin,
  onClearArchetype,
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
  goToNextStep: () => void;
  skipOnboarding?: () => void;
  isLastContentStep: boolean;
  canContinue: boolean;
  isNewOnboarding: boolean;
  isAdmin: boolean;
  onClearArchetype: () => void;
}) {
  const isSmall = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();

  const footerBackdropProps: CSSProperties = isSmall
    ? {
        height: "96px",
        padding: "24px",
        background: "rgba(20, 21, 23, 0.80)",
        backdropFilter: "blur(18px)",
        boxShadow: "0px 4px 24px 0px var(--chakra-colors-transparent-black-40)",
        borderTop: "1px solid var(--chakra-colors-transparent-white-20)",
      }
    : { padding: isNewOnboarding ? "8px 48px 48px 48px" : "24px" };
  return (
    <Flex
      width="100%"
      flexShrink={0}
      justifyContent="space-between"
      alignItems="center"
      style={{ ...footerBackdropProps }}
    >
      <Flex gap="16px" width="100%">
        {activeStep > 0 && (
          <Button
            variant="tertiary"
            size="lg"
            onClick={() => setActiveStep(activeStep - 1)}
          >
            {t("components.onboarding.back")}
          </Button>
        )}
        <Button
          id="btnOnboardingNext"
          variant="primary"
          size="lg"
          onClick={goToNextStep}
          display="flex"
          flexGrow={isMobile || isNewOnboarding ? 1 : undefined}
          isDisabled={!canContinue}
          maxWidth="100%"
        >
          {isLastContentStep
            ? isNewOnboarding
              ? t("components.onboarding.archetype.save")
              : t("components.onboarding.finish")
            : t("components.onboarding.continue")}
        </Button>
      </Flex>
      {isMobile || !skipOnboarding ? null : (
        <Button
          gc-testing-id="btnOnboardingSkip"
          variant="iconOnly"
          onClick={skipOnboarding}
        >
          <Text variant="onboardingSkip">
            {t("components.onboarding.skip")}
          </Text>
        </Button>
      )}
      {isAdmin && isNewOnboarding && (
        <Button
          variant="tertiary"
          size="lg"
          onClick={onClearArchetype}
        >
          Clear Archetype
        </Button>
      )}
    </Flex>
  );
}
